// Palette type definitions
type TextStatusPaletteConfig = {
  white: string;
  black: string;
  action: string;
  success: string;
  error: string;
};

type SurfacePaletteConfig = {
  surf1: string;
  surf2: string;
  surf3: string;
  surf4: string;
  surf5: string;
  surf6: string;
  surf7: string;
  surf8: string;
  surf9: string;
};

type GraySurfacePaletteConfig = {
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
};

// TypeScript declarations for custom palette properties
declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
    textStatus: TextStatusPaletteConfig;
    systemSurface: SurfacePaletteConfig;
    graySurface: GraySurfacePaletteConfig;
    storeFooterSurface: string;
    storeFooterText: string;
    shippingBanner: string;
    icon: string;
  }
  interface PaletteOptions {
    tertiary: PaletteOptions['primary'];
    textStatus?: TextStatusPaletteConfig;
    systemSurface?: SurfacePaletteConfig;
    graySurface?: GraySurfacePaletteConfig;
    storeFooterSurface?: string;
    storeFooterText?: string;
    shippingBanner?: string;
    icon?: string;
  }
}

// Button color overrides
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

// System colors for light mode
export const systemColorsLight = {
  background: {
    default: '#FFFFFF', // surf2
    paper: '#FFFFFF', // surf2
  },
  text: {
    primary: '#3F3F3F',
    secondary: '#575757',
  },
  action: {
    active: '#3F3F3F', // text.primary for icons
  },
  textStatus: {
    white: '#FFFFFF',
    black: '#000000',
    action: '#007AFE',
    success: '#0B963F',
    error: '#E02C2C',
  },
  systemSurface: {
    surf1: '#000000', // banners
    surf2: '#FFFFFF',
    surf3: '#F3F2F8',
    surf4: '#F5F5F5',
    surf5: '#E3E3E3',
    surf6: '#6E6E6E', // icons
    surf7: '#0B963F',
    surf8: '#E02C2C',
    surf9: '#F5B65E',
  },
  graySurface: {
    gray100: '#F5F5F5',
    gray200: '#ECECEC',
    gray300: '#E3E3E3',
    gray400: '#D1D1D1',
    gray500: '#BEBEBE',
    gray600: '#ACACAC',
  },
};

// System colors for dark mode
export const systemColorsDark = {
  background: {
    default: '#121212', // surf3
    paper: '#121212', // surf3
  },
  text: {
    primary: '#BBBABA',
    secondary: '#A6A6A6',
    disabled: '#504F55',
  },
  action: {
    active: '#BBBABA', // text.primary for icons
  },
  textStatus: {
    white: '#1C1C1E',
    black: '#EEEEEE',
    action: '#419CFF',
    success: '#60AD7D',
    error: '#DE6060',
  },
  systemSurface: {
    surf1: '#D1D1D1', // banners
    surf2: '#1C1C1E',
    surf3: '#121212',
    surf4: '#28272B',
    surf5: '#39383D',
    surf6: '#9C9B9B', // icons
    surf7: '#60AD7D',
    surf8: '#DE6060',
    surf9: '#D19B4F',
  },
  graySurface: {
    gray100: '#28272B',
    gray200: '#333337',
    gray300: '#39383D',
    gray400: '#434248',
    gray500: '#504F55',
    gray600: '#615F66',
  },
};

// Brand theme: Wayroo
export const wayrooTheme = {
  colorSchemes: {
    light: {
      palette: {
        ...systemColorsLight,
        primary: {
          main: '#5656D8',
        },
        secondary: {
          main: '#5656D8',
        },
        // active tab
        tertiary: {
          main: '#5656D8',
        },
        storeFooterSurface: '#19193F',
        storeFooterText: '#F5B65E',
        shippingBanner: '#19193F',
        icon: '#EC1F63',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
    dark: {
      palette: {
        ...systemColorsDark,
        primary: {
          main: '#8E8EE6',
        },
        secondary: {
          main: '#8E8EE6',
        },
        // active tab
        tertiary: {
          main: '#8E8EE6',
        },
        storeFooterSurface: '#19193F',
        storeFooterText: '#D19B4F',
        shippingBanner: '#8E8EE6',
        icon: '#EE4C82',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
  },
};

// Brand theme: Paparazzi
export const paparazziTheme = {
  colorSchemes: {
    light: {
      palette: {
        ...systemColorsLight,
        primary: {
          main: '#000000',
        },
        secondary: {
          main: '#EC008C',
        },
        // active tab
        tertiary: {
          main: '#EC008C',
        },
        storeFooterSurface: '#484949',
        storeFooterText: '#FFFFFF',
        shippingBanner: '#000000',
        icon: '#000000',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
    dark: {
      palette: {
        ...systemColorsDark,
        primary: {
          main: '#FFFFFF',
        },
        secondary: {
          main: '#F03293',
        },
        // active tab
        tertiary: {
          main: '#F03293',
        },
        storeFooterSurface: '#484949',
        storeFooterText: '#FFFFFF',
        shippingBanner: '#FFFFFF',
        icon: '#FFFFFF',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
  },
};

// Brand theme: Jordan Essentials
export const jordanEssentialsTheme = {
  colorSchemes: {
    light: {
      palette: {
        ...systemColorsLight,
        primary: {
          main: '#4B7939',
        },
        secondary: {
          main: '#EA6B8B',
        },
        // active tab
        tertiary: {
          main: '#4B7939',
        },
        storeFooterSurface: '#333333',
        storeFooterText: '#FFFFFF',
        shippingBanner: '#4B7939',
        icon: '#EA6B8B',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
    dark: {
      palette: {
        ...systemColorsDark,
        primary: {
          main: '#72A951',
        },
        secondary: {
          main: '#FFC1CC',
        },
        // active tab
        tertiary: {
          main: '#72A951',
        },
        storeFooterSurface: '#676767',
        storeFooterText: '#FFFFFF',
        shippingBanner: '#72A951',
        icon: '#FFC1CC',
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#04CE00',
        },
      },
    },
  },
};

// Backward compatibility export for test files
export const wayrooThemeLight = {
  palette: {
    mode: 'light' as const,
    ...wayrooTheme.colorSchemes.light.palette,
  },
};
